import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchProductSalesOrderById,
  createProductSalesOrder,
  updateProductSalesOrder,
  fetchServiceSalesOrderById,
  createServiceSalesOrder,
  updateServiceSalesOrder,
  fetchAllProductSalesOrders,
  fetchAllServiceSalesOrders,
  fetchIssuePackingSalesOrderStatusUpdate,
  handleRevertProductSalesOrderStatus,
  handleRevertServiceSalesOrderStatus,
  fetchProductSalesOrderDropdownForQuotationNumber,
  fetchServiceSalesOrderDropdownForQuotationNumber,
  fetchServiceQuotationById,
  fetchProductQuotationById,
  handleProductGeneratePISalesOrder,
  handleServiceGeneratePISalesOrder,
  serviceSalesOrderServiceCompleted,
  handleProductDownloadAcknowledgementSalesOrder
} from "./thunk";

/* ==============================
   TYPES – MATCH API RESPONSE
================================ */

interface AssignedMember {
  user_id: string;
  username: string;
  first_name: string;
  last_name: string;
}

interface SalesOrderProductItem {
  sales_order_product_item_id: string;
  product_name: string;
  category_name: string;
  sub_category_name: string;
  make_name: string;
  model_name: string;
  product_quantity: number;
  unit_price: string;
  product_total: string;
}

interface CustomerDetails {
  customer_id: string;
  customer_name: string;
  company_name: string;
  gstin: string;
  phone_number: string;
  email: string;
}

export interface SalesOrderStateData {
  sales_order_product_id: string;
  sales_order_number: string;
  sales_order_date: string;
  sales_order_status: string;
  agent_name: string;

  customer_details: CustomerDetails;
  assigned_members: AssignedMember[];
  sales_order_product_items: SalesOrderProductItem[];
}

/* ==============================
   STATE
================================ */

interface SalesOrderState {
  loading: boolean;
  error: string | null;
  currentSalesOrder: SalesOrderStateData | null;
  salesOrdersList: SalesOrderStateData[];
  rowCount: number;
  operationSuccess: boolean;
  issuePackingSalesOrderStatusUpdate: any;
  salesOrderDropdownForQuotationNumber: any[];
}

const initialState: SalesOrderState = {
  loading: false,
  error: null,
  currentSalesOrder: null,
  salesOrdersList: [],
  rowCount: 0,
  operationSuccess: false,
  issuePackingSalesOrderStatusUpdate: null,
  salesOrderDropdownForQuotationNumber: [],
};

/* ==============================
   SLICE
================================ */

const salesOrderSlice = createSlice({
  name: "SalesOrder",
  initialState,
  reducers: {
    clearSalesOrderState: (state) => {
      state.loading = false;
      state.error = null;
      state.currentSalesOrder = null;
      state.operationSuccess = false;
      state.issuePackingSalesOrderStatusUpdate = null;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: SalesOrderState) => {
      state.loading = true;
      state.error = null;
      state.operationSuccess = false;
    };

    const handleRejected = (
      state: SalesOrderState,
      action: PayloadAction<any>
    ) => {
      state.loading = false;
      state.error = action.payload || "Something went wrong";
      state.operationSuccess = false;
    };

    /* ==============================
       PRODUCT SALES ORDER
    ================================ */

    builder
      .addCase(fetchProductSalesOrderById.pending, handlePending)
      .addCase(
        fetchProductSalesOrderById.fulfilled,
        (state, action: PayloadAction<SalesOrderStateData>) => {
          state.loading = false;
          state.currentSalesOrder = action.payload;
        }
      )
      .addCase(fetchProductSalesOrderById.rejected, handleRejected);

    builder
      .addCase(createProductSalesOrder.pending, handlePending)
      .addCase(createProductSalesOrder.fulfilled, (state) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(createProductSalesOrder.rejected, handleRejected);

    builder
      .addCase(updateProductSalesOrder.pending, handlePending)
      .addCase(
        updateProductSalesOrder.fulfilled,
        (state, action: PayloadAction<SalesOrderStateData>) => {
          state.loading = false;
          state.currentSalesOrder = action.payload;
          state.operationSuccess = true;
        }
      )
      .addCase(updateProductSalesOrder.rejected, handleRejected);

    /* ==============================
       SERVICE SALES ORDER
    ================================ */

    builder
      .addCase(fetchServiceSalesOrderById.pending, handlePending)
      .addCase(
        fetchServiceSalesOrderById.fulfilled,
        (state, action: PayloadAction<SalesOrderStateData>) => {
          state.loading = false;
          state.currentSalesOrder = action.payload;
        }
      )
      .addCase(fetchServiceSalesOrderById.rejected, handleRejected);

    builder
      .addCase(createServiceSalesOrder.pending, handlePending)
      .addCase(createServiceSalesOrder.fulfilled, (state) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(createServiceSalesOrder.rejected, handleRejected);

    builder
      .addCase(updateServiceSalesOrder.pending, handlePending)
      .addCase(
        updateServiceSalesOrder.fulfilled,
        (state, action: PayloadAction<SalesOrderStateData>) => {
          state.loading = false;
          state.currentSalesOrder = action.payload;
          state.operationSuccess = true;
        }
      )
      .addCase(updateServiceSalesOrder.rejected, handleRejected);

    /* ==============================
       FETCH ALL (LIST)
    ================================ */

    builder
      .addCase(fetchAllProductSalesOrders.pending, handlePending)
      .addCase(
        fetchAllProductSalesOrders.fulfilled,
        (state, action: PayloadAction<{ data: { data: any[]; count: any }; rowCount: any }>) => {
          state.loading = false;
          state.salesOrdersList = action.payload.data.data;
          state.error = null;
        }
      )
      .addCase(fetchAllProductSalesOrders.rejected, handleRejected);

    builder
      .addCase(fetchAllServiceSalesOrders.pending, handlePending)
      .addCase(
        fetchAllServiceSalesOrders.fulfilled,
        (
          state,
          action: PayloadAction<SalesOrderStateData[]>
        ) => {
          state.loading = false;
          state.salesOrdersList = action.payload;
        }
      )
      .addCase(fetchAllServiceSalesOrders.rejected, handleRejected);

    /* ==============================
       ISSUE PACKING STATUS
    ================================ */

    builder
      .addCase(fetchIssuePackingSalesOrderStatusUpdate.pending, handlePending)
      .addCase(
        fetchIssuePackingSalesOrderStatusUpdate.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.issuePackingSalesOrderStatusUpdate = action.payload;
        }
      )
      .addCase(fetchIssuePackingSalesOrderStatusUpdate.rejected, handleRejected);


    /* ==============================
       REVERT SALES ORDER STATUS
    ================================ */

    builder
      .addCase(handleRevertProductSalesOrderStatus.pending, handlePending)
      .addCase(handleRevertProductSalesOrderStatus.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(handleRevertProductSalesOrderStatus.rejected, handleRejected);

    builder
      .addCase(handleRevertServiceSalesOrderStatus.pending, handlePending)
      .addCase(handleRevertServiceSalesOrderStatus.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(handleRevertServiceSalesOrderStatus.rejected, handleRejected);

    /* ==============================
       FETCH PRODUCT SALES ORDER DROPDOWN FOR QUOTATION NUMBER
    ================================ */

    builder
      .addCase(fetchProductSalesOrderDropdownForQuotationNumber.pending, handlePending)
      .addCase(fetchProductSalesOrderDropdownForQuotationNumber.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.salesOrderDropdownForQuotationNumber = action.payload;
      })
      .addCase(fetchProductSalesOrderDropdownForQuotationNumber.rejected, handleRejected);

    /* ==============================
       FETCH SERVICE SALES ORDER DROPDOWN FOR QUOTATION NUMBER
    ================================ */

    builder
      .addCase(fetchServiceSalesOrderDropdownForQuotationNumber.pending, handlePending)
      .addCase(fetchServiceSalesOrderDropdownForQuotationNumber.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.salesOrderDropdownForQuotationNumber = action.payload;
      })
      .addCase(fetchServiceSalesOrderDropdownForQuotationNumber.rejected, handleRejected);

    /* ==============================
       FETCH PRODUCT QUOTATION BY ID
    ================================ */
    builder
      .addCase(fetchProductQuotationById.pending, handlePending)
      .addCase(fetchProductQuotationById.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.currentSalesOrder = action.payload;
      })
      .addCase(fetchProductQuotationById.rejected, handleRejected);

    /* ==============================
       FETCH SERVICE QUOTATION BY ID
    ================================ */
    builder
      .addCase(fetchServiceQuotationById.pending, handlePending)
      .addCase(fetchServiceQuotationById.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.currentSalesOrder = action.payload;
      })
      .addCase(fetchServiceQuotationById.rejected, handleRejected);

    /* ==============================
       HANDLE PRODUCT GENERATE PI SALES ORDER
    ================================ */
    builder
      .addCase(handleProductGeneratePISalesOrder.pending, handlePending)
      .addCase(handleProductGeneratePISalesOrder.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(handleProductGeneratePISalesOrder.rejected, handleRejected);

    /* ==============================
       HANDLE SERVICE GENERATE PI SALES ORDER
    ================================ */
    builder
      .addCase(handleServiceGeneratePISalesOrder.pending, handlePending)
      .addCase(handleServiceGeneratePISalesOrder.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(handleServiceGeneratePISalesOrder.rejected, handleRejected);

    /* ==============================
       HANDLE SERVICE SALES ORDER SERVICE COMPLETED
    ================================ */
    builder
      .addCase(serviceSalesOrderServiceCompleted.pending, handlePending)
      .addCase(serviceSalesOrderServiceCompleted.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(serviceSalesOrderServiceCompleted.rejected, handleRejected);
    /* ==============================
       HANDLE PRODUCT DOWNLOAD ACKNOWLEDGEMENT SALES ORDER
    ================================ */
    builder
      .addCase(handleProductDownloadAcknowledgementSalesOrder.pending, handlePending)
      .addCase(handleProductDownloadAcknowledgementSalesOrder.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.operationSuccess = true;
      })
      .addCase(handleProductDownloadAcknowledgementSalesOrder.rejected, handleRejected);
    }
});





export const { clearSalesOrderState } = salesOrderSlice.actions;
export default salesOrderSlice.reducer;
